import React, { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { Wrench, Tractor, TrendingUp } from "lucide-react";
import { useI18n } from "../lib/i18n";

const fmt = (d) => d.toISOString().slice(0, 10);

function BarList({ title, data, unit = "h", testid }) {
  const { t } = useI18n();
  const max = Math.max(1, ...data.map((d) => d.hours));
  return (
    <div className="bg-white border border-stone-200 p-4 shadow-sm" data-testid={testid}>
      <h3 className="text-sm font-bold uppercase tracking-wide mb-3">{title}</h3>
      {data.length === 0 ? (
        <div className="text-xs text-stone-400 uppercase">{t("er.no_data")}</div>
      ) : (
        <div className="space-y-2">
          {data.slice(0, 12).map((d) => (
            <div key={d.label} className="text-xs">
              <div className="flex justify-between mb-0.5"><span className="font-medium truncate pr-2">{d.label}</span><span className="font-mono font-bold">{d.hours}{unit}</span></div>
              <div className="h-2 bg-stone-100"><div className="h-2 bg-[#7C9673]" style={{ width: `${(d.hours / max) * 100}%` }} /></div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function EquipmentReport() {
  const { t } = useI18n();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [ranches, setRanches] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [equipment, setEquipment] = useState([]);

  const now = new Date();
  const [start, setStart] = useState(fmt(new Date(now.getFullYear(), now.getMonth(), 1)));
  const [end, setEnd] = useState(fmt(now));
  const [fEmp, setFEmp] = useState("");
  const [fRanch, setFRanch] = useState("");
  const [fJob, setFJob] = useState("");
  const [fEquip, setFEquip] = useState("");
  const [fType, setFType] = useState("");

  useEffect(() => {
    (async () => {
      const [e, r, j, eq] = await Promise.all([api.get("/employees"), api.get("/ranches"), api.get("/jobcodes"), api.get("/equipment")]);
      setEmployees(e.data); setRanches(r.data); setJobs(j.data); setEquipment(eq.data);
    })();
  }, []);

  const types = useMemo(() => [...new Set(equipment.map((e) => e.category).filter(Boolean))].sort(), [equipment]);

  const run = async () => {
    setLoading(true);
    const params = new URLSearchParams({ start_date: start, end_date: end });
    if (fEmp) params.set("employee_id", fEmp);
    if (fRanch) params.set("ranch_id", fRanch);
    if (fJob) params.set("job_code_id", fJob);
    if (fEquip) params.set("equipment_id", fEquip);
    if (fType) params.set("equipment_type", fType);
    try {
      const { data } = await api.get(`/reports/equipment?${params.toString()}`);
      setReport(data);
    } catch { /* ignore */ } finally { setLoading(false); }
  };
  useEffect(() => { run(); /* eslint-disable-next-line */ }, []);

  return (
    <div className="space-y-6" data-testid="equipment-report-page">
      <div>
        <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight" style={{ fontFamily: "'Fraunces', serif" }}>{t("er.title")}</h1>
        <p className="text-sm font-medium tracking-wide uppercase text-stone-500">{t("er.sub")}</p>
      </div>

      <div className="bg-white border border-stone-200 p-4 shadow-sm grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 items-end">
        <div><label className="text-[10px] font-bold uppercase block mb-1">{t("ts.start")}</label><input data-testid="er-start" type="date" value={start} onChange={(e) => setStart(e.target.value)} className="w-full h-10 px-2 border border-stone-200 text-sm" /></div>
        <div><label className="text-[10px] font-bold uppercase block mb-1">{t("ts.end")}</label><input data-testid="er-end" type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="w-full h-10 px-2 border border-stone-200 text-sm" /></div>
        <select data-testid="er-emp" value={fEmp} onChange={(e) => setFEmp(e.target.value)} className="h-10 px-2 border border-stone-200 text-sm"><option value="">{t("er.all_employees")}</option>{employees.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}</select>
        <select data-testid="er-ranch" value={fRanch} onChange={(e) => setFRanch(e.target.value)} className="h-10 px-2 border border-stone-200 text-sm"><option value="">{t("er.all_properties")}</option>{ranches.map((x) => <option key={x.id} value={x.id}>{x.ranch_number} · {x.name}{x.block ? ` · ${x.block}` : ""}</option>)}</select>
        <select data-testid="er-job" value={fJob} onChange={(e) => setFJob(e.target.value)} className="h-10 px-2 border border-stone-200 text-sm"><option value="">{t("er.all_jobs")}</option>{jobs.map((x) => <option key={x.id} value={x.id}>{x.job_code || x.code} · {x.job || x.description}</option>)}</select>
        <select data-testid="er-type" value={fType} onChange={(e) => setFType(e.target.value)} className="h-10 px-2 border border-stone-200 text-sm"><option value="">{t("er.all_types")}</option>{types.map((x) => <option key={x} value={x}>{x}</option>)}</select>
        <select data-testid="er-equip" value={fEquip} onChange={(e) => setFEquip(e.target.value)} className="h-10 px-2 border border-stone-200 text-sm"><option value="">{t("er.all_equipment")}</option>{equipment.map((x) => <option key={x.id} value={x.id}>{x.code} · {x.name}</option>)}</select>
      </div>
      <button data-testid="er-run" onClick={run} className="h-11 px-6 bg-[#1F3D34] text-white font-bold uppercase text-sm">{loading ? t("common.loading") : t("er.run")}</button>

      {report && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div className="bg-[#1F3D34] text-white p-5" data-testid="er-total-hours"><div className="text-xs uppercase tracking-wide opacity-80 flex items-center gap-1"><Wrench size={14} /> {t("er.total_hours")}</div><div className="text-4xl font-bold mt-1">{report.total_equipment_hours}</div></div>
            <div className="bg-white border border-stone-200 p-5"><div className="text-xs uppercase tracking-wide text-stone-500 flex items-center gap-1"><Tractor size={14} /> {t("er.machines")}</div><div className="text-4xl font-bold mt-1">{report.by_equipment.length}</div></div>
            <div className="bg-white border border-stone-200 p-5"><div className="text-xs uppercase tracking-wide text-stone-500 flex items-center gap-1"><TrendingUp size={14} /> {t("er.segments")}</div><div className="text-4xl font-bold mt-1">{report.entry_count}</div></div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <BarList title={t("er.by_equipment")} data={report.by_equipment} testid="er-by-equipment" />
            <BarList title={t("er.by_employee")} data={report.by_employee} testid="er-by-employee" />
            <BarList title={t("er.by_job")} data={report.by_job_code} testid="er-by-job" />
            <BarList title={t("er.by_property")} data={report.by_property} testid="er-by-property" />
            <BarList title={t("er.by_type")} data={report.by_type} testid="er-by-type" />
            <BarList title={t("er.by_week")} data={report.by_week} testid="er-by-week" />
          </div>

          <div className="bg-white border border-stone-200 shadow-sm" data-testid="er-utilization">
            <h3 className="text-sm font-bold uppercase tracking-wide p-3 border-b border-stone-200">{t("er.utilization")}</h3>
            <table className="w-full text-sm">
              <thead className="bg-stone-50 text-left text-xs"><tr><th className="p-2">{t("er.equipment")}</th><th className="p-2">{t("er.hours")}</th><th className="p-2">{t("er.operators")}</th></tr></thead>
              <tbody>
                {report.utilization.length === 0 ? <tr><td colSpan={3} className="p-4 text-center text-stone-400 uppercase text-xs">{t("er.no_data")}</td></tr> :
                  report.utilization.map((u) => (
                    <tr key={u.equipment} className="border-b border-stone-100"><td className="p-2 font-mono">{u.equipment}</td><td className="p-2 font-bold">{u.hours}h</td><td className="p-2">{u.operators}</td></tr>
                  ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
